import React, { useEffect, useState } from "react";
import api, { apiError, openFile, isPdfFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  Plus, Eye, Download, RefreshCw, Upload, Trash2, History, Image, Loader2,
  CheckCircle2, Circle, FileText, ChevronDown,
} from "lucide-react";

export default function FolderDialog({ companyId, folder, open, onClose, onChanged }) {
  const [records, setRecords] = useState(null);
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState({});
  const isDated = !!folder?.deadline_type;

  const empty = { name: "", description: "", subtype: "", deadline: "", publish: true, file: null };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!folder) return;
    api.get(`/companies/${companyId}/folders/${folder.index}/records`).then((r) => setRecords(r.data));
  };
  useEffect(() => { if (open && folder) { setRecords(null); setAdding(false); setForm(empty); load(); } // eslint-disable-next-line
  }, [open, folder]);

  const create = async () => {
    if (!form.name.trim() || !form.file) { toast.error("Belge adı ve dosya zorunludur"); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("name", form.name); fd.append("catalog_id", folder.index);
    fd.append("description", form.description); fd.append("subtype", form.subtype);
    fd.append("deadline", form.deadline); fd.append("publish", form.publish);
    fd.append("file", form.file);
    try {
      await api.post(`/companies/${companyId}/records`, fd);
      toast.success("Belge eklendi"); setForm(empty); setAdding(false); load(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePublish = async (r) => {
    try {
      await api.put(`/records/${r.id}`, { is_published: !r.is_published });
      toast.success(r.is_published ? "Yayından kaldırıldı (taslak)" : "Yayımlandı"); load(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const renew = async (r, file, deadline) => {
    const fd = new FormData(); fd.append("file", file); fd.append("deadline", deadline);
    try { await api.post(`/records/${r.id}/renew`, fd); toast.success("Belge yenilendi, yeni sürüm ve tarih kaydedildi"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const newVersion = async (r, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/records/${r.id}/version`, fd); toast.success("Yeni sürüm yüklendi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addPhoto = async (r, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/records/${r.id}/photos`, fd); toast.success("Fotoğraf eklendi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm(`"${r.name}" arşivleriyle birlikte silinsin mi?`)) return;
    try { await api.delete(`/records/${r.id}`); toast.success("Silindi"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" /> {folder?.title}
          </DialogTitle>
          {isDated && <p className="text-xs text-amber-700">Süre takipli klasör: {folder.deadline_label}</p>}
        </DialogHeader>

        {!adding ? (
          <Button onClick={() => setAdding(true)} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="folder-add-record-button">
            <Plus className="mr-2 h-4 w-4" /> Bu Klasöre Belge Ekle
          </Button>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h4 className="mb-3 font-semibold text-[#0a192f]">Yeni Belge</h4>
            <div className="space-y-3">
              <div><Label>Belge Adı *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="record-name-input" className="mt-1 bg-white" /></div>
              <div><Label>Açıklama</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white" /></div>
              {folder?.subtypes && (
                <div><Label>Alt Tür</Label>
                  <Select value={form.subtype} onValueChange={(v) => setForm({ ...form, subtype: v })}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="record-subtype-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>{folder.subtypes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {isDated && (
                <div><Label>Son Tarih {`(${folder.deadline_label})`}</Label>
                  <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="record-deadline-input" className="mt-1 bg-white" /></div>
              )}
              <div><Label>Belge Dosyası (PDF / Word / Excel) *</Label><Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} data-testid="record-file-input" className="mt-1 bg-white" /></div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.publish} onChange={(e) => setForm({ ...form, publish: e.target.checked })} data-testid="record-publish-checkbox" />
                Hemen yayımla (müşteri görebilsin)
              </label>
              <div className="flex gap-2">
                <Button onClick={create} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="record-save-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
                </Button>
                <Button variant="outline" onClick={() => { setAdding(false); setForm(empty); }}>Vazgeç</Button>
              </div>
            </div>
          </div>
        )}

        {records === null ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : records.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Bu klasörde belge yok.</p>
        ) : (
          <div className="space-y-3">
            {records.map((r) => (
              <RecordItem key={r.id} r={r} isDated={isDated} folder={folder}
                expanded={!!expanded[r.id]} toggleExp={() => setExpanded({ ...expanded, [r.id]: !expanded[r.id] })}
                onView={(vid) => openFile(`/files/${vid}/download`)}
                onDownload={(vid, fn) => openFile(`/files/${vid}/download`, true, fn)}
                onPhoto={(vid) => openFile(`/photos/${vid}/download`)}
                togglePublish={togglePublish} renew={renew} newVersion={newVersion} addPhoto={addPhoto} del={del} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RecordItem({ r, isDated, folder, expanded, toggleExp, onView, onDownload, onPhoto, togglePublish, renew, newVersion, addPhoto, del }) {
  const [renewMode, setRenewMode] = useState(false);
  const [rf, setRf] = useState(null);
  const [rd, setRd] = useState("");
  const showPhotos = folder?.deadline_type === "egitim_tatbikat";

  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`record-item-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[#0a192f]">{r.name}</span>
            {r.subtype && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{r.subtype}</span>}
            {r.is_published
              ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />Yayımlandı</span>
              : <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Circle className="h-3.5 w-3.5" />Taslak</span>}
          </div>
          {r.description && <p className="text-sm text-slate-500">{r.description}</p>}
          {r.current && <p className="mt-1 text-xs text-slate-400">Güncel: {r.current.filename}{r.current.deadline ? ` · Son tarih: ${r.current.deadline}` : ""}</p>}
          {isDated && r.status && <div className="mt-2"><StatusBadge status={r.status} /></div>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {r.current && isPdfFile(r.current.filename) && <Button size="sm" variant="outline" onClick={() => onView(r.current.version_id)} data-testid={`record-view-${r.id}`}><Eye className="h-4 w-4" /></Button>}
          {r.current && <Button size="sm" variant="outline" onClick={() => onDownload(r.current.version_id, r.current.filename)} data-testid={`record-download-${r.id}`}><Download className="h-4 w-4" /></Button>}
          <Button size="sm" variant="outline" onClick={() => togglePublish(r)} data-testid={`record-publish-toggle-${r.id}`}>
            {r.is_published ? "Yayından kaldır" : "Yayımla"}
          </Button>
          <Button size="sm" variant="ghost" onClick={toggleExp}><ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} /></Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-3">
          {isDated ? (
            renewMode ? (
              <div className="rounded-lg bg-amber-50 p-3">
                <p className="mb-2 text-xs font-semibold text-amber-800">Yenileme: yeni dosya ve yeni son tarih birlikte gerekli</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input type="date" value={rd} onChange={(e) => setRd(e.target.value)} data-testid={`renew-deadline-${r.id}`} className="bg-white" />
                  <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => setRf(e.target.files[0])} data-testid={`renew-file-${r.id}`} className="bg-white" />
                </div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`renew-save-${r.id}`}
                    onClick={() => { if (!rf || !rd) { toast.error("Dosya ve tarih zorunlu"); return; } renew(r, rf, rd); setRenewMode(false); setRf(null); setRd(""); }}>
                    <RefreshCw className="mr-1 h-4 w-4" /> Yenile
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRenewMode(false)}>Vazgeç</Button>
                </div>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setRenewMode(true)} data-testid={`record-renew-button-${r.id}`}>
                <RefreshCw className="mr-1 h-4 w-4" /> Yenile (yeni dosya + tarih)
              </Button>
            )
          ) : (
            <label className="inline-flex">
              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" className="hidden" onChange={(e) => e.target.files[0] && newVersion(r, e.target.files[0])} />
              <span className="inline-flex cursor-pointer items-center rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"><Upload className="mr-1 h-4 w-4" /> Yeni sürüm yükle</span>
            </label>
          )}

          {showPhotos && (
            <label className="inline-flex">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && addPhoto(r, e.target.files[0])} />
              <span className="ml-2 inline-flex cursor-pointer items-center rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"><Image className="mr-1 h-4 w-4" /> Fotoğraf ekle</span>
            </label>
          )}

          {r.photos && r.photos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {r.photos.map((p) => (
                <button key={p.id} onClick={() => onPhoto(p.id)} className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50">
                  <Image className="mr-1 inline h-3 w-3" />{p.filename}
                </button>
              ))}
            </div>
          )}

          <div className="rounded-lg bg-slate-50 p-3">
            <p className="mb-2 flex items-center gap-1 text-xs font-semibold text-slate-600"><History className="h-3.5 w-3.5" /> Sürüm Arşivi ({r.version_count})</p>
            <div className="space-y-1">
              {r.versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between text-xs" data-testid={`version-${v.id}`}>
                  <span className={v.is_current ? "font-semibold text-emerald-700" : "text-slate-500"}>
                    v{v.version_no} · {v.filename}{v.deadline ? ` · ${v.deadline}` : ""}{v.is_current ? " (güncel)" : ""}
                  </span>
                  <button onClick={() => onView(v.id)} className="text-emerald-700 hover:underline">{isPdfFile(v.filename) ? "Görüntüle" : "İndir"}</button>
                </div>
              ))}
            </div>
          </div>

          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => del(r)} data-testid={`record-delete-${r.id}`}>
            <Trash2 className="mr-1 h-4 w-4" /> Sil
          </Button>
        </div>
      )}
    </div>
  );
}
